// import components
import { Button, Menu, MenuItem, SxProps } from '@mui/material';

// import hooks
import { useState } from 'react';
import { useSelector } from 'hooks';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { setUserPreference } from 'redux/actionCreators';
import { Icon } from '@sdb/planning-ui';
import { ZoomLevel } from './ZoomLevelMenu.enums';

type Props = {
  disabled?: boolean;
  disableZoomLevels?: ZoomLevel[] | null;
  minimized?: boolean;
  onSelect?: (value: ZoomLevel) => void;
  sx?: SxProps;
};

const ZoomLevelMenu = (props: Props) => {
  const { disabled, disableZoomLevels = [], onSelect, minimized, sx } = props;

  const dispatch = useDispatch();
  const zoomLevelPreference = useSelector(
    (state) => state.preferences.defaultZoomLevel
  );
  // Certain zoom levels can be disabled, in those cases we use the first available zoom level
  const defaultZoomLevel =
    zoomLevelPreference && !disableZoomLevels?.includes(zoomLevelPreference)
      ? zoomLevelPreference
      : Object.keys(ZoomLevel).find(
          (v) => !disableZoomLevels?.includes(parseInt(v) as ZoomLevel)
        );

  const { t } = useTranslation('common', {
    keyPrefix: 'activityPlanning.zoomLevel'
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = !!anchorEl;
  const handleOnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleOnSelect = (value: ZoomLevel) => {
    handleOnClose();
    onSelect && onSelect(value);
    dispatch(
      setUserPreference({
        key: 'defaultZoomLevel',
        value
      })
    );
  };

  const handleOnClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleOnClick}
        disabled={disabled}
        sx={{
          display: 'flex',
          gap: (theme) => theme.spacing(2),
          zIndex: 31,
          ...(sx || {})
        }}
      >
        <Icon type="magnifying-glass-plus" fontSize="small" />
        {!minimized &&
          t(ZoomLevel[defaultZoomLevel ?? ZoomLevel.STANDARD].toLowerCase())}
        {!disabled && <Icon type="chevron-down" fontSize="small" />}
      </Button>

      {!disabled && (
        <Menu open={open} anchorEl={anchorEl} onClose={handleOnClose}>
          {(
            Object.values(ZoomLevel).filter(
              (v: string | ZoomLevel) =>
                Number.isInteger(v) &&
                !disableZoomLevels?.includes(v as ZoomLevel)
            ) as ZoomLevel[]
          ).map((v: ZoomLevel, i: number) => (
            <MenuItem
              key={i}
              onClick={handleOnSelect.bind(this, v)}
              sx={{
                fontFamily:
                  v == defaultZoomLevel
                    ? 'SourceSansPro-Bold'
                    : 'SourceSansPro-Regular'
              }}
            >
              {(() => {
                switch (v as ZoomLevel) {
                  case ZoomLevel.COMPACT:
                    return t('compact');
                  case ZoomLevel.STANDARD:
                    return t('standard');
                  case ZoomLevel.DETAILED:
                    return t('detailed');
                }
              })()}
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
};

export default ZoomLevelMenu;
